import { type CSSProperties, useMemo } from "react";
import { Outlet, useNavigation } from "react-router";

import { RequireAccess } from "@/components/auth/require-access";
import { PreferencesProvider } from "@/components/providers/preferences-provider";
import { AppSidebar } from "@/components/shell/app-sidebar";
import { BreadcrumbProvider } from "@/components/shell/breadcrumbs";
import { SiteHeader } from "@/components/shell/site-header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { AbilityProvider, abilityFor } from "@/lib/auth/ability";
import { useSession } from "@/lib/auth/auth-provider";
import { useLiveOrderAlerts } from "@/lib/live-order-alerts";
import { readPreferences, readSidebarOpen } from "@/lib/preferences";
import { cn } from "@/lib/utils";

export function DashboardLayout() {
  const session = useSession();
  // Route chunks load on demand; show a thin bar while the next page is on its way.
  const navigating = useNavigation().state === "loading";
  const ability = useMemo(() => abilityFor(session.role), [session.role]);
  useLiveOrderAlerts();

  return (
    <AbilityProvider value={ability}>
      <PreferencesProvider initial={readPreferences()}>
        <SidebarProvider
          defaultOpen={readSidebarOpen()}
          style={{ "--sidebar-width": "calc(var(--spacing) * 64)" } as CSSProperties}
        >
          <div
            aria-hidden="true"
            className={cn(
              "bg-primary fixed inset-x-0 top-0 z-50 h-0.5 origin-left transition-transform duration-500",
              navigating ? "scale-x-75" : "scale-x-0 duration-0",
            )}
          />
          <AppSidebar user={{ name: session.name, email: session.email, role: session.role }} />
          <SidebarInset className="min-w-0 overflow-x-clip">
            <BreadcrumbProvider>
              <SiteHeader />
              <div className="flex min-w-0 flex-1 flex-col p-4 md:p-6">
                <RequireAccess role={session.role}>
                  <Outlet />
                </RequireAccess>
              </div>
            </BreadcrumbProvider>
          </SidebarInset>
        </SidebarProvider>
      </PreferencesProvider>
    </AbilityProvider>
  );
}
